import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { USAGE_LIMIT_BANNER } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { ASSIGN_SEAT, PRICING_PAGE_REDIRECT, REQUEST_SEAT } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import HubTypes from 'self-service-api/constants/HubTypes';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { getProductNameTextFromApiName } from '../../_core/common/adapters/getProductNameText';
import { getUpgradeDataFromUpgradeConfig } from '../../_core/utils/getUpgradeDataFromUpgradeConfig';
import displayAddOnLimitIncreaseCopy from '../displayAddOnLimitIncreaseCopy';
import { getBannerI18nSubTitleKey, getBannerI18nTitleKey, getHubAndTierFromApiName } from '../utils';
import DefaultBanner from './DefaultBanner';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSeatTypeKey = apiName => {
  const {
    hub,
    tier
  } = getHubAndTierFromApiName(apiName);
  if (tier === STARTER.toLocaleLowerCase()) {
    return `CORE_STARTER`;
  }
  if (hub === HubTypes.SALES.toLocaleLowerCase()) {
    return `${HubTypes.SALES}_${tier.toLocaleUpperCase()}`;
  } else if (hub === HubTypes.SERVICE.toLocaleLowerCase()) {
    return `${HubTypes.SERVICE}_${tier.toLocaleUpperCase()}`;
  }
  return `CORE_${tier.toLocaleUpperCase()}`;
};
export const UsageLimitBannerTitle = ({
  featureTranslationKey,
  value,
  limit
}) => {
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: getBannerI18nTitleKey('upgrades.usageLimitBanner.header', featureTranslationKey),
    options: {
      value,
      limit
    }
  });
};
export const UsageLimitBannerSubtext = ({
  apiName,
  featureTranslationKey,
  purchaseMotions
}) => {
  const translatedProductName = getProductNameTextFromApiName(apiName);
  const addOnCopyOverride = displayAddOnLimitIncreaseCopy(apiName);
  const seatsCopyOverride = purchaseMotions ? purchaseMotions.filter(pm => pm === ASSIGN_SEAT || pm === REQUEST_SEAT).length > 0 : false;
  if (seatsCopyOverride) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext.seats`, featureTranslationKey)
      }), "\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
        message: `upgrades.seats.${getSeatTypeKey(apiName)}`
      }), "."]
    });
  }
  if (addOnCopyOverride) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
      }), "\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
        message: `upgrades.usageLimitBanner.addOnLimitIncreaseOverride`,
        options: {
          productName: translatedProductName
        }
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
    }), "\xA0", translatedProductName && /*#__PURE__*/_jsx(FormattedMessage, {
      message: translatedProductName ? `upgrades.message.productName` : '',
      options: {
        productName: translatedProductName
      }
    })]
  });
};
const UsageLimitBanner = ({
  upgradePoint,
  limit,
  value,
  displayThreshold,
  hasReachedLimit,
  marginStyle,
  'data-test-id': dataTestId = 'usage-limit-banner',
  purchaseMotionData,
  displayBannerOverride = false
}) => {
  const [purchaseMotions, setPurchaseMotions] = useState([]);
  const upgradeData = getUpgradeDataFromUpgradeConfig(upgradePoint);
  useEffect(() => {
    const fallbackPurchaseMotions = [PRICING_PAGE_REDIRECT];
    if (!upgradeData.apiName || !purchaseMotionData || !purchaseMotionData.locations || !purchaseMotionData.locations.USAGE_LIMIT_BANNER || !purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions) {
      setPurchaseMotions(fallbackPurchaseMotions);
    } else {
      // already have purchase motions from props so transform those instead of fetching
      const usageLimitBannerPMs = purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions;
      const rawPurchaseMotions = usageLimitBannerPMs[upgradeData.apiName];
      if (rawPurchaseMotions) {
        const transformedPurchaseMotions = Object.values(rawPurchaseMotions).flat().map(obj => obj ? obj.key : obj);
        setPurchaseMotions(transformedPurchaseMotions);
      } else {
        setPurchaseMotions(fallbackPurchaseMotions);
      }
    }
  }, [purchaseMotionData, upgradeData.apiName]);
  const usageLimitBannerData = {
    limit,
    value,
    displayThreshold,
    hasReachedLimit
  };
  if (!displayBannerOverride && displayThreshold && value < displayThreshold) return null;
  return /*#__PURE__*/_jsx(DefaultBanner, {
    titleText: /*#__PURE__*/_jsx(UsageLimitBannerTitle, {
      featureTranslationKey: upgradePoint.featureTranslationKey,
      limit: limit,
      value: value
    }),
    marginStyle: marginStyle,
    dataTestId: dataTestId,
    subtext: /*#__PURE__*/_jsx(UsageLimitBannerSubtext, {
      apiName: upgradeData.apiName,
      featureTranslationKey: upgradePoint.featureTranslationKey,
      purchaseMotions: purchaseMotions
    }),
    button: /*#__PURE__*/_jsx(UIPurchaseMotionButton, {
      upgradeData: upgradeData,
      usageLimitBannerData: usageLimitBannerData,
      location: USAGE_LIMIT_BANNER,
      level: 1,
      use: "link",
      purchaseMotions: purchaseMotions,
      "data-test-id": "upgrade-button",
      goToUpgradePage: true,
      truncate: false,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.upgradeButton.default"
      })
    })
  });
};
export default UsageLimitBanner;